import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useCompartilhamentos, CompartilhadaBadge } from './CompartilhadaBadge';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  DollarSign, 
  AlertTriangle,
  CheckCircle,
  User,
  Calendar,
  Clock,
  CreditCard,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  MessageSquare,
  Car,
  Phone,
  Mail,
  MapPin,
  History,
  Receipt,
  TrendingUp,
  PiggyBank,
  Loader2
} from 'lucide-react';
import { apiService } from '../../services/api';
import { LembreteAtrasoButton } from '../pagamentos/LembreteAtrasoButton';

export const PagamentosPage = () => {
  const compartilhamentos = useCompartilhamentos();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [pagamentos, setPagamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [motos, setMotos] = useState([]);
  const [locacoes, setLocacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados para filtros e pesquisa
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [pesquisa, setPesquisa] = useState('');
  const [activeTab, setActiveTab] = useState('todos');
  
  // Estados para modais
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [pagamentoSelecionado, setPagamentoSelecionado] = useState(null);
  
  // Estados para modal de marcar como pago
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [pagamentoPago, setPagamentoPago] = useState(null);
  const [valoresAtraso, setValoresAtraso] = useState(null);
  const [calculandoAtraso, setCalculandoAtraso] = useState(false);
  const [processandoPagamento, setProcessandoPagamento] = useState(false);
  
  // Estados para modal de débitos
  const [showDebitosModal, setShowDebitosModal] = useState(false);
  const [debitosPendentes, setDebitosPendentes] = useState(null);
  const [carregandoDebitos, setCarregandoDebitos] = useState(false);
  const [valorDebitoParcial, setValorDebitoParcial] = useState('');
  const [usarValorParcial, setUsarValorParcial] = useState(false);
  
  // Estado para modal de confirmação extra
  const [showConfirmacaoModal, setShowConfirmacaoModal] = useState(false);
  const [acaoConfirmacao, setAcaoConfirmacao] = useState(null); // {tipo: 'pago_simples' | 'pago_juros' | 'pago_debitos', dados: {...}}
  
  // Estados para formulário
  const [formData, setFormData] = useState({
    locacao_id: '',
    valor: '',
    data_vencimento: '',
    semana_referencia: '1',
    observacoes: '',
    tipo: 'semanal'
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const [pagamentosData, clientesData, motosData, locacoesData] = await Promise.all([
        apiService.getPagamentos(),
        apiService.getClientes(),
        apiService.getMotos(),
        apiService.getLocacoes()
      ]);
      
      setPagamentos(pagamentosData || []);
      setClientes(clientesData || []);
      setMotos(motosData || []);
      setLocacoes(locacoesData || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Enriquecer pagamentos com dados de cliente e moto
  const enriquecerPagamentos = (pagamentosArray) => {
    return pagamentosArray.map(pagamento => {
      const locacao = locacoes.find(l => l.id === pagamento.locacao_id);
      const cliente = clientes.find(c => c.id === locacao?.cliente_id);
      const moto = motos.find(m => m.id === locacao?.moto_id);
      
      return {
        ...pagamento,
        cliente: cliente || {},
        moto: moto || {},
        locacao: locacao || {}
      };
    });
  };

  const pagamentosEnriquecidos = enriquecerPagamentos(pagamentos);

  // Função para verificar se pagamento está atrasado
  const verificarSeAtrasado = (pagamento) => {
    if (pagamento.status === 'pago') return false;
    
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    let vencimento;
    if (typeof pagamento.data_vencimento === 'string' && pagamento.data_vencimento.match(/^\d{4}-\d{2}-\d{2}/)) {
      const [year, month, day] = pagamento.data_vencimento.split('T')[0].split('-');
      vencimento = new Date(year, month - 1, day);
    } else {
      vencimento = new Date(pagamento.data_vencimento);
    }
    vencimento.setHours(0, 0, 0, 0);
    
    return hoje > vencimento;
  };

  // Função para abrir modal de marcar como pago
  const abrirModalPago = async (pagamento) => {
    const pag = pagamentosEnriquecidos.find(p => p.id === pagamento.id) || pagamento;
    setPagamentoPago(pag);
    
    // Primeiro verifica se há débitos pendentes
    setCarregandoDebitos(true);
    try {
      if (pag.locacao_id) {
        const debitos = await apiService.getDebitosPendentesLocacao(pag.locacao_id);
        if (debitos && debitos.total_debitos > 0) {
          setDebitosPendentes(debitos);
          
          // Se também está atrasado, calcular valores de atraso
          if (verificarSeAtrasado(pag)) {
            setCalculandoAtraso(true);
            try {
              const valores = await apiService.calcularValoresAtraso(pag.id);
              setValoresAtraso(valores);
            } catch (error) {
              console.error('Erro ao calcular valores de atraso:', error);
              setValoresAtraso(null);
            } finally {
              setCalculandoAtraso(false);
            }
          }
          
          setShowDebitosModal(true);
          return;
        }
      }
    } catch (error) {
      console.error('Erro ao verificar débitos:', error);
    } finally {
      setCarregandoDebitos(false);
    }
    
    // Se não há débitos, continuar com fluxo normal (verificar atraso)
    if (verificarSeAtrasado(pag)) {
      // Pagamento atrasado - calcular valores
      setCalculandoAtraso(true);
      setShowPagoModal(true);
      try {
        const valores = await apiService.calcularValoresAtraso(pag.id);
        setValoresAtraso(valores);
      } catch (error) {
        console.error('Erro ao calcular valores:', error);
        setValoresAtraso(null);
      } finally {
        setCalculandoAtraso(false);
      }
    } else {
      // Pagamento não atrasado - mostrar confirmação antes de marcar
      solicitarConfirmacao('pago_simples', { pagamentoId: pag.id, pagamento: pag });
    }
  };

  // Solicitar confirmação antes de executar ação
  const solicitarConfirmacao = (tipo, dados) => {
    setAcaoConfirmacao({ tipo, dados });
    setShowConfirmacaoModal(true);
  };

  // Executar ação após confirmação
  const executarAcaoConfirmada = async () => {
    if (!acaoConfirmacao) return;
    
    setShowConfirmacaoModal(false);
    
    switch (acaoConfirmacao.tipo) {
      case 'pago_simples':
        await executarPagoSimples(acaoConfirmacao.dados.pagamentoId);
        break;
      case 'pago_juros':
        await executarPagoComJuros();
        break;
      case 'pago_debitos':
        await executarPagamentoComDebitos(acaoConfirmacao.dados.tipoDebito);
        break;
      default:
        break;
    }
    
    setAcaoConfirmacao(null);
  };

  // Cancelar confirmação
  const cancelarConfirmacao = () => {
    setShowConfirmacaoModal(false);
    setAcaoConfirmacao(null);
  };

  // Marcar como pago sem juros (valor original) - execução real
  const executarPagoSimples = async (pagamentoId) => {
    try {
      await apiService.marcarPagamentoPago(pagamentoId);
      setShowPagoModal(false);
      setPagamentoPago(null);
      setValoresAtraso(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao marcar pagamento como pago:', error);
      alert('Erro ao marcar pagamento como pago. Tente novamente.');
    }
  };

  // Marcar como pago sem juros - com confirmação
  const marcarComoPagoSimples = async (pagamentoId) => {
    solicitarConfirmacao('pago_simples', { pagamentoId, pagamento: pagamentoPago });
  };

  // Marcar como pago COM juros - com confirmação
  const marcarComoPagoComJuros = async () => {
    if (!pagamentoPago || !valoresAtraso) return;
    solicitarConfirmacao('pago_juros', { pagamento: pagamentoPago, valores: valoresAtraso });
  };

  // Executar pago com juros após confirmação
  const executarPagoComJuros = async () => {
    if (!pagamentoPago || !valoresAtraso) return;
    
    setProcessandoPagamento(true);
    try {
      await apiService.marcarPagamentoPagoComJuros(pagamentoPago.id, {
        valor_pago: valoresAtraso.valor_total,
        valor_original: valoresAtraso.valor_original,
        multa: valoresAtraso.multa,
        juros: valoresAtraso.juros,
        dias_atraso: valoresAtraso.dias_atraso
      });
      setShowPagoModal(false);
      setPagamentoPago(null);
      setValoresAtraso(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao marcar pagamento como pago com juros:', error);
      alert('Erro ao marcar pagamento como pago. Tente novamente.');
    } finally {
      setProcessandoPagamento(false);
    }
  };

  // Confirmar pagamento COM débitos - com confirmação
  const confirmarPagamentoComDebitos = async (tipoDebito) => {
    if (!pagamentoPago || !debitosPendentes) return;
    solicitarConfirmacao('pago_debitos', { tipoDebito });
  };

  // Executar pagamento com débitos após confirmação
  const executarPagamentoComDebitos = async (tipoDebito) => {
    // tipoDebito: 'total' (paga parcela completa), 'parcial' (paga valor informado), 'sem' (não paga débito)
    if (!pagamentoPago || !debitosPendentes) return;
    
    setProcessandoPagamento(true);
    try {
      const valorAluguel = pagamentoPago.valor_aluguel || pagamentoPago.valor || 0;
      const valorDebitosParcela = debitosPendentes.parcela_semanal || 0;
      const multa = valoresAtraso?.multa || 0;
      const juros = valoresAtraso?.juros || 0;
      
      let valorDebitoPago = 0;
      let redividirDebitos = false;
      
      if (tipoDebito === 'total') {
        valorDebitoPago = valorDebitosParcela;
      } else if (tipoDebito === 'parcial') {
        valorDebitoPago = parseFloat(valorDebitoParcial) || 0;
        redividirDebitos = true; // Quando paga parcial, re-divide o saldo por 4
      }
      // tipoDebito === 'sem' -> valorDebitoPago = 0
      
      const valorPago = valorAluguel + valorDebitoPago + multa + juros;
      
      await apiService.confirmarPagamentoComDebitos(pagamentoPago.id, {
        pago_com_debitos: valorDebitoPago > 0,
        valor_pago: valorPago,
        valor_aluguel: valorAluguel,
        valor_debitos_parcela: valorDebitosParcela,
        valor_debitos_pago: valorDebitoPago,
        total_debitos_cliente: debitosPendentes.total_debitos,
        redividir_debitos: redividirDebitos,
        multa: multa,
        juros: juros,
        dias_atraso: valoresAtraso?.dias_atraso || 0
      });
      
      setShowDebitosModal(false);
      setPagamentoPago(null);
      setDebitosPendentes(null);
      setValoresAtraso(null);
      setValorDebitoParcial('');
      setUsarValorParcial(false);
      carregarDados();
      
      // Mensagem de sucesso
      let msg = '';
      if (tipoDebito === 'total') {
        msg = `Pagamento confirmado! Débitos de R$ ${valorDebitosParcela.toFixed(2)} foram abatidos.`;
      } else if (tipoDebito === 'parcial') {
        msg = `Pagamento confirmado! Débitos de R$ ${valorDebitoPago.toFixed(2)} foram abatidos. Saldo restante será re-dividido em 4 parcelas.`;
      } else {
        msg = `Pagamento confirmado! Os débitos serão somados ao próximo pagamento.`;
      }
      alert(msg);
    } catch (error) {
      console.error('Erro ao confirmar pagamento:', error);
      alert('Erro ao confirmar pagamento. Tente novamente.');
    } finally {
      setProcessandoPagamento(false);
    }
  };

  const marcarComoPago = async (pagamentoId) => {
    try {
      await apiService.marcarPagamentoPago(pagamentoId);
      carregarDados();
    } catch (error) {
      console.error('Erro ao marcar pagamento como pago:', error);
      alert('Erro ao marcar pagamento como pago. Tente novamente.');
    }
  };

  const excluirPagamento = async (pagamentoId) => {
    if (!confirm('Tem certeza que deseja excluir este pagamento?')) {
      return;
    }

    try {
      await apiService.excluirPagamento(pagamentoId);
      alert('Pagamento excluído com sucesso!');
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir pagamento:', error);
      alert('Erro ao excluir pagamento. Tente novamente.');
    }
  };

  const adicionarPagamento = async (e) => {
    e.preventDefault();
    try {
      await apiService.criarPagamento(formData);
      alert('Pagamento adicionado com sucesso!');
      setShowAddModal(false);
      setFormData({
        locacao_id: '',
        valor: '',
        data_vencimento: '',
        semana_referencia: '1',
        observacoes: '',
        tipo: 'semanal'
      });
      carregarDados();
    } catch (error) {
      console.error('Erro ao adicionar pagamento:', error);
      alert('Erro ao adicionar pagamento. Tente novamente.');
    }
  };

  const editarPagamento = async (e) => {
    e.preventDefault();
    try {
      // 1. Atualizar o pagamento individual
      await apiService.atualizarPagamento(pagamentoSelecionado.id, formData);
      
      // 2. Se o valor semanal foi alterado, propagar para a locação e pagamentos futuros
      if (formData.valor_semanal && pagamentoSelecionado.locacao_id) {
        const novoVS = parseFloat(formData.valor_semanal);
        if (novoVS > 0) {
          try {
            const res = await apiService.api.patch(`/locacoes/${pagamentoSelecionado.locacao_id}/valor-semanal`, {
              valor_semanal: novoVS
            });
            const data = res.data;
            alert(`Pagamento editado! Valor semanal atualizado para R$ ${novoVS.toFixed(2)}. ${data.pagamentos_atualizados} cobrança(s) futura(s) atualizada(s).`);
          } catch (err) {
            console.error('Erro ao propagar valor semanal:', err);
            alert('Pagamento editado, mas houve erro ao propagar valor semanal para cobranças futuras.');
          }
        }
      } else {
        alert('Pagamento editado com sucesso!');
      }
      
      setShowEditModal(false);
      setPagamentoSelecionado(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao editar pagamento:', error);
      alert('Erro ao editar pagamento. Tente novamente.');
    }
  };

  const abrirModalEdicao = (pagamento) => {
    setPagamentoSelecionado(pagamento);
    const valorAluguel = pagamento.valor_aluguel || pagamento.valor - (pagamento.valor_debitos_parcela || 0);
    setFormData({
      locacao_id: pagamento.locacao_id || '',
      valor: pagamento.valor || '',
      valor_semanal: valorAluguel > 0 ? parseFloat(valorAluguel).toFixed(2) : '',
      data_vencimento: pagamento.data_vencimento?.split('T')[0] || '',
      semana_referencia: pagamento.semana_referencia || '1',
      observacoes: pagamento.observacoes || '',
      tipo: pagamento.tipo || 'semanal'
    });
    setShowEditModal(true);
  };

  const abrirDetalhes = (pagamento) => {
    setPagamentoSelecionado(pagamento);
    setShowDetailsModal(true);
  };

  const enviarLembreteWhatsApp = async (pagamento) => {
    if (!pagamento.cliente.telefone) {
      alert('Cliente não possui número de telefone cadastrado.');
      return;
    }

    try {
      // Usar endpoint do backend que calcula tudo corretamente
      const response = await apiService.api.post(`/pagamentos/${pagamento.id}/enviar-lembrete-atraso`);
      const data = response.data;
      
      if (data.whatsapp_link) {
        window.open(data.whatsapp_link, '_blank');
      }
    } catch (error) {
      // Se o pagamento não está atrasado (vence no futuro), gerar lembrete preventivo
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('não venceu')) {
        try {
          const response = await apiService.api.post(`/pagamentos/${pagamento.id}/gerar-lembrete`);
          const data = response.data;
          if (data.whatsapp_link) {
            window.open(data.whatsapp_link, '_blank');
          }
        } catch (err) {
          console.error('Erro ao gerar lembrete preventivo:', err);
          alert('Erro ao gerar lembrete. Tente novamente.');
        }
      } else {
        console.error('Erro ao enviar lembrete:', error);
        alert('Erro ao enviar lembrete: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const exportarPagamentos = async () => {
    try {
      const filtros = {
        status: activeTab === 'todos' ? null : activeTab,
        // Adicionar mais filtros conforme necessário
      };
      
      const resultado = await apiService.exportarPagamentos(filtros);
      alert(`Arquivo exportado com sucesso: ${resultado.filename}`);
    } catch (error) {
      console.error('Erro ao exportar pagamentos:', error);
      alert('Erro ao exportar pagamentos. Tente novamente.');
    }
  };

  const getStatusFromDate = (pagamento) => {
    if (pagamento.status === 'pago') return 'pago';
    
    // Corrigir comparação de datas - usar apenas a data, sem horas
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    let vencimento;
    if (typeof pagamento.data_vencimento === 'string' && pagamento.data_vencimento.match(/^\d{4}-\d{2}-\d{2}/)) {
      const [year, month, day] = pagamento.data_vencimento.split('T')[0].split('-');
      vencimento = new Date(year, month - 1, day);
    } else {
      vencimento = new Date(pagamento.data_vencimento);
    }
    vencimento.setHours(0, 0, 0, 0);
    
    // Atrasado APENAS se venceu ANTES de hoje (não inclui hoje)
    if (hoje > vencimento) return 'atrasado';
    return 'pendente';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pago': return 'bg-green-100 text-green-800';
      case 'pendente': return 'bg-yellow-100 text-yellow-800';
      case 'atrasado': return 'bg-red-100 text-red-800';
      case 'cancelado': return 'bg-asphalt-700 text-white';
      default: return 'bg-asphalt-700 text-white';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pago': return 'Pago';
      case 'pendente': return 'Pendente';
      case 'atrasado': return 'Atrasado';
      case 'cancelado': return 'Cancelado';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Corrigir problema de timezone - interpretar como data local, não UTC
      // Se a data é "2025-12-28", adicionar T12:00:00 para evitar problemas de timezone
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      // Para outros formatos, usar o método padrão mas com ajuste
      const date = new Date(dateString + 'T12:00:00');
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calculateDaysLate = (vencimento) => {
    if (!vencimento) return 0;
    // Corrigir problema de timezone
    // Extrair ano, mês, dia diretamente da string para evitar conversão UTC
    let dataVencimento;
    if (typeof vencimento === 'string' && vencimento.match(/^\d{4}-\d{2}-\d{2}/)) {
      const [year, month, day] = vencimento.split('T')[0].split('-');
      dataVencimento = new Date(year, month - 1, day); // month é 0-indexed
    } else {
      dataVencimento = new Date(vencimento);
    }
    
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0); // Zerar horas para comparar só datas
    dataVencimento.setHours(0, 0, 0, 0);
    
    const diffTime = hoje - dataVencimento;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Filtrar pagamentos
  const filtrarPagamentos = (pagamentosArray) => {
    return pagamentosArray.filter(pagamento => {
      const statusAtual = getStatusFromDate(pagamento);
      const matchStatus = activeTab === 'todos' || statusAtual === activeTab;
      const matchPesquisa = pesquisa === '' || 
        pagamento.cliente.nome?.toLowerCase().includes(pesquisa.toLowerCase()) ||
        pagamento.moto.placa?.toLowerCase().includes(pesquisa.toLowerCase()) ||
        pagamento.cliente.cpf?.includes(pesquisa);
      
      return matchStatus && matchPesquisa;
    });
  };

  const pagamentosFiltrados = filtrarPagamentos(pagamentosEnriquecidos);

  // Separar por status para estatísticas
  const pagamentosPendentes = pagamentosEnriquecidos.filter(p => getStatusFromDate(p) === 'pendente');
  const pagamentosAtrasados = pagamentosEnriquecidos.filter(p => getStatusFromDate(p) === 'atrasado');
  const pagamentosPagos = pagamentosEnriquecidos.filter(p => p.status === 'pago');

  // Calcular totais
  const totalRecebido = pagamentosPagos.reduce((acc, p) => acc + (p.valor || 0), 0);
  const totalPendente = pagamentosPendentes.reduce((acc, p) => acc + (p.valor || 0), 0);
  const totalAtrasado = pagamentosAtrasados.reduce((acc, p) => acc + (p.valor || 0), 0);

  if (loading) {
    return <div className="flex justify-center p-8">Carregando pagamentos...</div>;
  }

  return (
    <div className="space-y-6 p-1" data-testid="pagamentos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Pagamentos</h2>
          <p className="text-sm text-gray-400 mt-1">Gerencie todas as cobranças e recebimentos</p>
        </div>
        
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 shadow-lg shadow-green-600/25">
              <Plus className="h-4 w-4 mr-2" />
              Nova Cobrança
            </Button>
          </DialogTrigger>
          
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Nova Cobrança</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={adicionarPagamento} className="space-y-4">
              <div>
                <Label htmlFor="locacao_id">Locação *</Label>
                <Select value={formData.locacao_id} onValueChange={(value) => setFormData({...formData, locacao_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma locação..." />
                  </SelectTrigger>
                  <SelectContent>
                    {locacoes.map(locacao => {
                      const cliente = clientes.find(c => c.id === locacao.cliente_id);
                      const moto = motos.find(m => m.id === locacao.moto_id);
                      return (
                        <SelectItem key={locacao.id} value={locacao.id}>
                          {cliente?.nome} - {moto?.placa} ({locacao.plano})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valor">Valor (R$) *</Label>
                  <Input
                    id="valor"
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({...formData, valor: e.target.value})}
                    placeholder="349.99"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="semana_referencia">Semana #</Label>
                  <Input
                    id="semana_referencia"
                    type="number"
                    min="1"
                    value={formData.semana_referencia}
                    onChange={(e) => setFormData({...formData, semana_referencia: e.target.value})}
                    placeholder="1"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="data_vencimento">Data de Vencimento *</Label>
                <Input
                  id="data_vencimento"
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Informações adicionais sobre a cobrança..."
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1">
                  Adicionar Cobrança
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Cards de resumo - Design Premium */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Recebido */}
        <div 
          onClick={() => setActiveTab('pago')}
          className={`group relative overflow-hidden cursor-pointer rounded-2xl h-32 transition-all duration-300 ${
            activeTab === 'pago' 
              ? 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-xl shadow-green-500/30 scale-[1.02]' 
              : 'bg-gradient-to-br from-emerald-500 to-green-600 opacity-80 hover:opacity-100 hover:shadow-lg'
          }`}
        >
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <CheckCircle className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
              <span className="text-xs font-medium text-white/80 bg-white/10 px-2 py-1 rounded-full">{pagamentosPagos.length} pag.</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{formatCurrency(totalRecebido)}</p>
              <p className="text-sm text-green-100">Recebido</p>
            </div>
          </div>
        </div>

        {/* Pendente */}
        <div 
          onClick={() => setActiveTab('pendente')}
          className={`group relative overflow-hidden cursor-pointer rounded-2xl h-32 transition-all duration-300 ${
            activeTab === 'pendente' 
              ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-xl shadow-amber-500/30 scale-[1.02]' 
              : 'bg-gradient-to-br from-amber-500 to-orange-600 opacity-80 hover:opacity-100 hover:shadow-lg'
          }`}
        >
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <Clock className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <span className="text-xs font-medium text-white/80 bg-white/10 px-2 py-1 rounded-full">{pagamentosPendentes.length} pag.</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{formatCurrency(totalPendente)}</p>
              <p className="text-sm text-amber-100">Pendente</p>
            </div>
          </div>
        </div>

        {/* Em Atraso */}
        <div 
          onClick={() => setActiveTab('atrasado')}
          className={`group relative overflow-hidden cursor-pointer rounded-2xl h-32 transition-all duration-300 ${
            activeTab === 'atrasado' 
              ? 'bg-gradient-to-br from-rose-500 to-red-600 shadow-xl shadow-rose-500/30 scale-[1.02]' 
              : 'bg-gradient-to-br from-rose-500 to-red-600 opacity-80 hover:opacity-100 hover:shadow-lg'
          }`}
        >
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <AlertTriangle className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <span className="text-xs font-medium text-white/80 bg-white/10 px-2 py-1 rounded-full">{pagamentosAtrasados.length} pag.</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{formatCurrency(totalAtrasado)}</p>
              <p className="text-sm text-rose-100">Em Atraso</p>
            </div>
          </div>
        </div>

        {/* Total Geral */}
        <div 
          onClick={() => setActiveTab('todos')}
          className={`group relative overflow-hidden cursor-pointer rounded-2xl h-32 transition-all duration-300 ${
            activeTab === 'todos' 
              ? 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-xl shadow-blue-500/30 scale-[1.02]' 
              : 'bg-gradient-to-br from-blue-500 to-indigo-600 opacity-80 hover:opacity-100 hover:shadow-lg'
          }`}
        >
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <DollarSign className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <span className="text-xs font-medium text-white/80 bg-white/10 px-2 py-1 rounded-full">{pagamentosEnriquecidos.length} total</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{formatCurrency(totalRecebido + totalPendente + totalAtrasado)}</p>
              <p className="text-sm text-blue-100">Total Geral</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e pesquisa modernizados */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-slate-800/80 rounded-xl p-4 shadow-sm border border-white/10">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Pesquisar por cliente, CPF ou placa..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            className="pl-11 h-11 bg-slate-700/50 border-white/10 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-500 rounded-xl font-medium"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportarPagamentos} className="rounded-xl border-white/20 text-gray-200 hover:bg-white/10 font-semibold">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Tabs de status modernizadas */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-slate-800/80 rounded-xl shadow-sm border border-white/10 overflow-hidden">
        <TabsList className="w-full overflow-x-auto flex bg-slate-700/50 p-1.5 rounded-t-xl gap-1 scrollbar-hide">
          <TabsTrigger value="todos" className="flex-shrink-0 rounded-lg data-[state=active]:bg-slate-800 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-300 font-medium transition-all text-xs px-3 py-2 whitespace-nowrap">
            Todos ({pagamentosEnriquecidos.length})
          </TabsTrigger>
          <TabsTrigger value="pendente" className="flex-shrink-0 rounded-lg data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-300 data-[state=active]:shadow-md text-gray-300 font-medium transition-all text-xs px-3 py-2 whitespace-nowrap">
            Pendentes ({pagamentosPendentes.length})
          </TabsTrigger>
          <TabsTrigger value="atrasado" className="flex-shrink-0 rounded-lg data-[state=active]:bg-red-500/20 data-[state=active]:text-red-300 data-[state=active]:shadow-md text-gray-300 font-medium transition-all text-xs px-3 py-2 whitespace-nowrap">
            Atrasados ({pagamentosAtrasados.length})
          </TabsTrigger>
          <TabsTrigger value="pago" className="flex-shrink-0 rounded-lg data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300 data-[state=active]:shadow-md text-gray-300 font-medium transition-all text-xs px-3 py-2 whitespace-nowrap">
            Pagos ({pagamentosPagos.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="p-4 space-y-4">
          {pagamentosFiltrados.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-700/50 rounded-full mb-4 border border-white/10">
                <Receipt className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {pesquisa ? 'Nenhum resultado encontrado' : `Nenhum pagamento ${activeTab === 'todos' ? '' : activeTab}`}
              </h3>
              <p className="text-gray-400 max-w-sm mx-auto">
                {pesquisa ? 'Tente ajustar sua pesquisa' : 'Adicione uma nova cobrança para começar'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {pagamentosFiltrados.map((pagamento) => {
                const statusAtual = getStatusFromDate(pagamento);
                return (
                  <div 
                    key={pagamento.id} 
                    className={`bg-slate-800/90 rounded-xl border-l-4 p-4 hover:shadow-lg transition-all border border-white/5 ${
                      statusAtual === 'pago' ? 'border-l-emerald-500' :
                      statusAtual === 'atrasado' ? 'border-l-red-500' :
                      'border-l-amber-500'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      {/* Info do Cliente */}
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border ${
                          statusAtual === 'pago' ? 'bg-emerald-500/20 border-emerald-500/30' :
                          statusAtual === 'atrasado' ? 'bg-red-500/20 border-red-500/30' :
                          'bg-amber-500/20 border-amber-500/30'
                        }`}>
                          <User className={`h-5 w-5 ${
                            statusAtual === 'pago' ? 'text-emerald-400' :
                            statusAtual === 'atrasado' ? 'text-red-400' :
                            'text-amber-400'
                          }`} />
                        </div>
                        
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-bold text-white truncate text-base">
                              {pagamento.cliente.nome || 'Cliente não informado'}
                            </h3>
                            <CompartilhadaBadge nomes={compartilhamentos.por_locacao[pagamento.locacao_id]} />
                            <Badge className={`text-xs font-semibold border ${
                              statusAtual === 'pago' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                              statusAtual === 'atrasado' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
                              'bg-amber-500/20 text-amber-300 border-amber-500/30'
                            }`}>
                              {getStatusText(statusAtual)}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-200">
                            <span className="flex items-center gap-1.5 font-medium">
                              <Car className="h-3.5 w-3.5 text-gray-400" />
                              {pagamento.moto.placa || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1.5 font-medium">
                              <Receipt className="h-3.5 w-3.5 text-gray-400" />
                              Semana {pagamento.semana_referencia}
                            </span>
                            <span className="flex items-center gap-1.5 font-medium">
                              <Calendar className="h-3.5 w-3.5 text-gray-400" />
                              {formatDate(pagamento.data_vencimento)}
                            </span>
                            {statusAtual === 'atrasado' && (
                              <span className="text-red-400 font-bold bg-red-500/10 px-2 py-0.5 rounded">
                                {calculateDaysLate(pagamento.data_vencimento)} dias
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Valor e Ações */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 lg:gap-4">
                        <div className="text-left sm:text-right">
                          <p className={`text-xl font-bold ${
                            statusAtual === 'pago' ? 'text-emerald-400' :
                            statusAtual === 'atrasado' ? 'text-red-400' :
                            'text-white'
                          }`}>
                            {formatCurrency(pagamento.valor || 0)}
                          </p>
                          {pagamento.valor_debitos_parcela > 0 && (
                            <p className="text-xs text-orange-400 font-medium">
                              Inclui R$ {pagamento.valor_debitos_parcela?.toFixed(2)} débitos
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 flex-wrap">
                          {statusAtual !== 'pago' && !isSubAdmin && (
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-500 rounded-lg shadow-sm font-semibold"
                              onClick={() => abrirModalPago(pagamento)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1.5" />
                              Pago
                            </Button>
                          )}
                          
                          {statusAtual === 'atrasado' && (
                            <LembreteAtrasoButton 
                              pagamento={pagamento}
                              onSuccess={() => carregarDados()}
                            />
                          )}
                          
                          {statusAtual === 'pendente' && pagamento.cliente.telefone && (
                            <Button 
                              size="sm"
                              variant="outline"
                              className="text-emerald-400 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg font-semibold"
                              onClick={() => enviarLembreteWhatsApp(pagamento)}
                            >
                              <MessageSquare className="h-4 w-4 mr-1.5" />
                              Lembrar
                            </Button>
                          )}
                          
                          {!isSubAdmin && (
                            <Button 
                              size="sm"
                              variant="ghost"
                              className="rounded-lg text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                              onClick={() => abrirModalEdicao(pagamento)}
                              title="Editar pagamento"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {!isSubAdmin && (
                            <Button 
                              size="sm"
                              variant="ghost"
                              className="rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={() => excluirPagamento(pagamento.id)}
                              title="Excluir pagamento"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          
                          <Button 
                            size="sm"
                            variant="ghost"
                            className="rounded-lg text-gray-300 hover:text-white hover:bg-white/10"
                            onClick={() => {
                              setPagamentoSelecionado(pagamento);
                              setShowDetailsModal(true);
                            }}
                            title="Ver detalhes"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal de Edição */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Cobrança</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={editarPagamento} className="space-y-4">
            <div>
              <Label>Cliente/Locação</Label>
              <p className="text-sm text-gray-400 bg-asphalt-700 p-2 rounded">
                {pagamentoSelecionado?.cliente?.nome} - {pagamentoSelecionado?.moto?.placa}
              </p>
            </div>
            
            {/* Valor Semanal do Contrato */}
            <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
              <Label htmlFor="edit_valor_semanal" className="text-emerald-400 font-semibold">Valor Semanal do Contrato (R$)</Label>
              <p className="text-xs text-gray-400 mb-2">Este valor será o padrão para todas as cobranças futuras.</p>
              <Input
                id="edit_valor_semanal"
                data-testid="input-edit-valor-semanal"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.valor_semanal || ''}
                onChange={(e) => setFormData({...formData, valor_semanal: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_valor">Valor Total desta Cobrança (R$) *</Label>
                <Input
                  id="edit_valor"
                  type="number"
                  step="0.01"
                  value={formData.valor}
                  onChange={(e) => setFormData({...formData, valor: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit_semana">Semana #</Label>
                <Input
                  id="edit_semana"
                  type="number"
                  min="1"
                  value={formData.semana_referencia}
                  onChange={(e) => setFormData({...formData, semana_referencia: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit_vencimento">Data de Vencimento *</Label>
              <Input
                id="edit_vencimento"
                type="date"
                value={formData.data_vencimento}
                onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit_observacoes">Observações</Label>
              <Textarea
                id="edit_observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1">
                Salvar Alterações
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowEditModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Detalhes do Pagamento - {formatCurrency(pagamentoSelecionado?.valor || 0)}
            </DialogTitle>
          </DialogHeader>
          
          {pagamentoSelecionado && (
            <div className="space-y-6">
              {/* Informações do Cliente */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-white mb-3 flex items-center">
                    <User className="h-5 w-5 mr-2 text-blue-600" />
                    Informações do Cliente
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-400">Nome:</span> {pagamentoSelecionado.cliente.nome || 'N/A'}</p>
                    <p><span className="text-gray-400">CPF:</span> {pagamentoSelecionado.cliente.cpf || 'N/A'}</p>
                    <p><span className="text-gray-400">Telefone:</span> {pagamentoSelecionado.cliente.telefone || 'N/A'}</p>
                    <p><span className="text-gray-400">Email:</span> {pagamentoSelecionado.cliente.email || 'N/A'}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white mb-3 flex items-center">
                    <Car className="h-5 w-5 mr-2 text-green-600" />
                    Informações da Moto
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-400">Placa:</span> {pagamentoSelecionado.moto.placa || 'N/A'}</p>
                    <p><span className="text-gray-400">Cor:</span> {pagamentoSelecionado.moto.cor || 'N/A'}</p>
                    <p><span className="text-gray-400">Ano:</span> {pagamentoSelecionado.moto.ano || 'N/A'}</p>
                    <p><span className="text-gray-400">Plano:</span> {pagamentoSelecionado.locacao.plano || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* Detalhes do Pagamento */}
              <div>
                <h3 className="font-semibold text-white mb-3 flex items-center">
                  <Receipt className="h-5 w-5 mr-2 text-purple-600" />
                  Detalhes do Pagamento
                </h3>
                <div className="bg-asphalt-700 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Valor:</span>
                      <p className="font-semibold text-lg">{formatCurrency(pagamentoSelecionado.valor || 0)}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Status:</span>
                      <p>
                        <Badge className={getStatusColor(getStatusFromDate(pagamentoSelecionado))}>
                          {getStatusText(getStatusFromDate(pagamentoSelecionado))}
                        </Badge>
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-400">Vencimento:</span>
                      <p className="font-medium">{formatDate(pagamentoSelecionado.data_vencimento)}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Semana:</span>
                      <p className="font-medium">{pagamentoSelecionado.semana_referencia}ª semana</p>
                    </div>
                    {pagamentoSelecionado.data_pagamento && (
                      <>
                        <div>
                          <span className="text-gray-400">Data do Pagamento:</span>
                          <p className="font-medium text-green-600">{formatDate(pagamentoSelecionado.data_pagamento)}</p>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {getStatusFromDate(pagamentoSelecionado) === 'atrasado' && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center text-red-800">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        <span className="font-medium">
                          {calculateDaysLate(pagamentoSelecionado.data_vencimento)} dias em atraso
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {pagamentoSelecionado.observacoes && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-800">
                        <strong>Observações:</strong> {pagamentoSelecionado.observacoes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Ações rápidas */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                {getStatusFromDate(pagamentoSelecionado) !== 'pago' && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      marcarComoPago(pagamentoSelecionado.id);
                      setShowDetailsModal(false);
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Marcar como Pago
                  </Button>
                )}
                
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowDetailsModal(false);
                    abrirModalEdicao(pagamentoSelecionado);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                
                <Button 
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    setShowDetailsModal(false);
                    excluirPagamento(pagamentoSelecionado.id);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Marcar como Pago - Pagamento Atrasado */}
      <Dialog open={showPagoModal} onOpenChange={(open) => {
        if (!open) {
          setShowPagoModal(false);
          setPagamentoPago(null);
          setValoresAtraso(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Marcar Pagamento como Pago
            </DialogTitle>
            <DialogDescription>
              Este pagamento está em atraso. Escolha como registrar o pagamento.
            </DialogDescription>
          </DialogHeader>

          {calculandoAtraso ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-3 text-gray-400">Calculando valores...</span>
            </div>
          ) : valoresAtraso && pagamentoPago ? (
            <div className="space-y-4">
              {/* Info do Cliente */}
              <div className="p-3 bg-asphalt-700 rounded-lg">
                <p className="font-medium">{pagamentoPago.cliente?.nome || 'Cliente'}</p>
                <p className="text-sm text-gray-400">Semana {pagamentoPago.semana_referencia || pagamentoPago.semana || 'N/A'}</p>
              </div>

              {/* Cálculo de Valores */}
              <div className="border border-white/10 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-white">💰 Valores:</h4>
                
                <div className="flex justify-between">
                  <span className="text-gray-400">Valor Original:</span>
                  <span className="font-medium">R$ {valoresAtraso.valor_original?.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-red-600">
                  <span>Multa:</span>
                  <span className="font-medium">+ R$ {valoresAtraso.multa?.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between text-red-600">
                  <span>Juros ({valoresAtraso.dias_atraso} dia(s)):</span>
                  <span className="font-medium">+ R$ {valoresAtraso.juros?.toFixed(2)}</span>
                </div>

                <div className="pt-3 border-t border-gray-300 flex justify-between">
                  <span className="font-bold text-lg">Total com Juros:</span>
                  <span className="font-bold text-lg text-green-600">R$ {valoresAtraso.valor_total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Alerta */}
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  <strong>Atenção:</strong> Selecione a opção correta para registrar o valor correto na receita.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="py-4 text-center text-gray-400">
              Erro ao carregar valores. Tente novamente.
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPagoModal(false);
                setPagamentoPago(null);
                setValoresAtraso(null);
              }}
              disabled={processandoPagamento}
            >
              Cancelar
            </Button>
            
            <Button
              variant="outline"
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
              onClick={() => {
                if (pagamentoPago) {
                  marcarComoPagoSimples(pagamentoPago.id);
                }
              }}
              disabled={calculandoAtraso || processandoPagamento || !valoresAtraso}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Pagou SEM Juros (R$ {valoresAtraso?.valor_original?.toFixed(2) || '0.00'})
            </Button>
            
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={marcarComoPagoComJuros}
              disabled={calculandoAtraso || processandoPagamento || !valoresAtraso}
            >
              {processandoPagamento ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Pagou COM Juros (R$ {valoresAtraso?.valor_total?.toFixed(2) || '0.00'})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Pagamento com Débitos */}
      <Dialog open={showDebitosModal} onOpenChange={(open) => {
        if (!open) {
          setShowDebitosModal(false);
          setPagamentoPago(null);
          setDebitosPendentes(null);
          setValoresAtraso(null);
          setValorDebitoParcial('');
          setUsarValorParcial(false);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-orange-600 text-base">
              <AlertTriangle className="h-4 w-4" />
              Pagamento com Débitos
            </DialogTitle>
            <DialogDescription className="text-xs">
              Cliente com débitos em aberto.
            </DialogDescription>
          </DialogHeader>

          {carregandoDebitos ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-3 text-gray-400">Carregando débitos...</span>
            </div>
          ) : debitosPendentes && pagamentoPago ? (
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {/* Info do Cliente - Compacto */}
              <div className="p-2 bg-asphalt-700 rounded-lg">
                <p className="font-medium text-sm">{pagamentoPago.cliente?.nome || 'Cliente'}</p>
                <p className="text-xs text-gray-400">
                  Semana {pagamentoPago.semana_referencia || pagamentoPago.semana || 'N/A'} - {pagamentoPago.moto?.placa}
                </p>
              </div>

              {/* Resumo dos Débitos - Compacto */}
              <div className="border border-orange-200 bg-orange-50 rounded-lg p-3">
                <h4 className="font-semibold text-orange-800 flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-3 w-3" />
                  Débitos: {debitosPendentes.quantidade_debitos}
                </h4>
                <div className="text-xs text-orange-700 mt-1 grid grid-cols-2 gap-1">
                  <span>Total:</span>
                  <span className="font-bold text-right">R$ {debitosPendentes.total_debitos?.toFixed(2)}</span>
                  <span>Parcela:</span>
                  <span className="font-bold text-right">R$ {debitosPendentes.parcela_semanal?.toFixed(2)}</span>
                  {debitosPendentes.parcelas_restantes > 0 && (
                    <>
                      <span>Restantes:</span>
                      <span className="text-right">{debitosPendentes.parcelas_restantes} parcela(s)</span>
                    </>
                  )}
                </div>
              </div>

              {/* Opção de valor parcial - Compacto */}
              <div className="border border-purple-200 bg-purple-50 rounded-lg p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={usarValorParcial}
                    onChange={(e) => {
                      setUsarValorParcial(e.target.checked);
                      if (!e.target.checked) setValorDebitoParcial('');
                    }}
                    className="w-4 h-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-xs font-medium text-purple-800">Valor diferente da parcela?</span>
                </label>
                
                {usarValorParcial && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-purple-600 font-medium text-sm">R$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={debitosPendentes.total_debitos}
                        value={valorDebitoParcial}
                        onChange={(e) => setValorDebitoParcial(e.target.value)}
                        placeholder="0,00"
                        className="flex-1 px-2 py-1.5 border border-purple-300 rounded-md text-sm focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    {valorDebitoParcial && parseFloat(valorDebitoParcial) > 0 && (
                      <p className="text-[10px] text-purple-600 mt-1">
                        Saldo: R$ {(debitosPendentes.total_debitos - parseFloat(valorDebitoParcial)).toFixed(2)} → 4x R$ {((debitosPendentes.total_debitos - parseFloat(valorDebitoParcial)) / 4).toFixed(2)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Valores do Pagamento - Compacto */}
              <div className="border border-white/10 rounded-lg p-3">
                <h4 className="font-semibold text-white text-sm mb-2">💰 Valores:</h4>
                
                <div className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Aluguel:</span>
                    <span className="font-medium">R$ {(pagamentoPago.valor_aluguel || pagamentoPago.valor || 0)?.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between text-orange-600">
                    <span>Débitos:</span>
                    <span className="font-medium">+ R$ {debitosPendentes.parcela_semanal?.toFixed(2)}</span>
                  </div>

                  {valoresAtraso && (valoresAtraso.multa > 0 || valoresAtraso.juros > 0) && (
                    <div className="flex justify-between text-red-600">
                      <span>Multa + Juros:</span>
                      <span className="font-medium">+ R$ {(valoresAtraso.multa + valoresAtraso.juros)?.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-white/10 space-y-1">
                    <div className="flex justify-between text-gray-300">
                      <span>Só semana:</span>
                      <span className="font-bold">
                        R$ {((pagamentoPago.valor_aluguel || pagamentoPago.valor || 0) + (valoresAtraso?.multa || 0) + (valoresAtraso?.juros || 0))?.toFixed(2)}
                      </span>
                    </div>
                    {usarValorParcial && valorDebitoParcial && parseFloat(valorDebitoParcial) > 0 ? (
                      <div className="flex justify-between text-purple-700">
                        <span className="font-bold">+ Parcial:</span>
                        <span className="font-bold">
                          R$ {((pagamentoPago.valor_aluguel || pagamentoPago.valor || 0) + parseFloat(valorDebitoParcial) + (valoresAtraso?.multa || 0) + (valoresAtraso?.juros || 0))?.toFixed(2)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between text-green-700">
                        <span className="font-bold">+ Débitos:</span>
                        <span className="font-bold">
                          R$ {((pagamentoPago.valor_aluguel || pagamentoPago.valor || 0) + debitosPendentes.parcela_semanal + (valoresAtraso?.multa || 0) + (valoresAtraso?.juros || 0))?.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-4 text-center text-gray-400">
              Erro ao carregar informações. Tente novamente.
            </div>
          )}

          <DialogFooter className="flex-shrink-0 flex flex-col gap-2 pt-3 border-t">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDebitosModal(false);
                  setPagamentoPago(null);
                  setDebitosPendentes(null);
                  setValoresAtraso(null);
                  setValorDebitoParcial('');
                  setUsarValorParcial(false);
                }}
                disabled={processandoPagamento}
                className="text-sm h-9"
              >
                Cancelar
              </Button>
              
              <Button
                variant="outline"
                className="border-blue-300 text-blue-600 hover:bg-blue-50 text-sm h-9"
                onClick={() => confirmarPagamentoComDebitos('sem')}
                disabled={carregandoDebitos || processandoPagamento || !debitosPendentes}
              >
                <DollarSign className="h-3 w-3 mr-1" />
                Só semana
              </Button>
              
              {usarValorParcial && valorDebitoParcial && parseFloat(valorDebitoParcial) > 0 ? (
                <Button
                  className="bg-purple-600 hover:bg-purple-700 text-sm h-9"
                  onClick={() => confirmarPagamentoComDebitos('parcial')}
                  disabled={carregandoDebitos || processandoPagamento || !debitosPendentes || parseFloat(valorDebitoParcial) <= 0}
                >
                  {processandoPagamento ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle className="h-3 w-3 mr-1" />
                  )}
                  R$ {parseFloat(valorDebitoParcial).toFixed(2)}
                </Button>
              ) : (
                <Button
                  className="bg-green-600 hover:bg-green-700 text-sm h-9"
                  onClick={() => confirmarPagamentoComDebitos('total')}
                  disabled={carregandoDebitos || processandoPagamento || !debitosPendentes}
                >
                  {processandoPagamento ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle className="h-3 w-3 mr-1" />
                  )}
                  + Débito
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação Extra */}
      <Dialog open={showConfirmacaoModal} onOpenChange={setShowConfirmacaoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Confirmação Necessária
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Esta ação não pode ser desfeita. Por favor, confirme que deseja continuar.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {acaoConfirmacao?.tipo === 'pago_simples' && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <p className="text-emerald-300 font-semibold mb-2">Marcar pagamento como PAGO?</p>
                <p className="text-gray-300 text-sm">
                  Cliente: <strong>{acaoConfirmacao.dados?.pagamento?.cliente?.nome || pagamentoPago?.cliente?.nome || 'N/A'}</strong>
                </p>
                <p className="text-gray-300 text-sm">
                  Valor: <strong className="text-emerald-400">
                    {formatCurrency(acaoConfirmacao.dados?.pagamento?.valor || pagamentoPago?.valor || 0)}
                  </strong>
                </p>
              </div>
            )}

            {acaoConfirmacao?.tipo === 'pago_juros' && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-300 font-semibold mb-2">Marcar pagamento como PAGO COM JUROS?</p>
                <p className="text-gray-300 text-sm">
                  Cliente: <strong>{pagamentoPago?.cliente?.nome || 'N/A'}</strong>
                </p>
                <p className="text-gray-300 text-sm">
                  Valor Original: <strong>{formatCurrency(valoresAtraso?.valor_original || 0)}</strong>
                </p>
                <p className="text-gray-300 text-sm">
                  Multa + Juros: <strong className="text-red-400">
                    + {formatCurrency((valoresAtraso?.multa || 0) + (valoresAtraso?.juros || 0))}
                  </strong>
                </p>
                <p className="text-amber-300 text-sm font-bold mt-2">
                  Total: {formatCurrency(valoresAtraso?.valor_total || 0)}
                </p>
              </div>
            )}

            {acaoConfirmacao?.tipo === 'pago_debitos' && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <p className="text-orange-300 font-semibold mb-2">Confirmar pagamento com débitos?</p>
                <p className="text-gray-300 text-sm">
                  Cliente: <strong>{pagamentoPago?.cliente?.nome || 'N/A'}</strong>
                </p>
                <p className="text-gray-300 text-sm">
                  Tipo: <strong>
                    {acaoConfirmacao.dados?.tipoDebito === 'total' ? 'Com parcela de débitos' : 
                     acaoConfirmacao.dados?.tipoDebito === 'parcial' ? 'Com valor parcial de débitos' : 
                     'Sem abater débitos'}
                  </strong>
                </p>
              </div>
            )}

            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-300 text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Atenção: Esta ação é irreversível!
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={cancelarConfirmacao}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={executarAcaoConfirmada}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 font-semibold"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Sim, Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};